/** Contacts reads — rec_contacts, READ-ONLY. Needs the SELECT policy from
 * MIGRATION_CRM2_PHASE6_READS.sql; until it runs, PostgREST returns 200+[]
 * (RLS lying-empty), so the page's empty state names the migration.
 *
 * Column mapping is v1's decompose (MIGRATION_PHASE3.sql contacts section):
 *   name=d.n · company=d.co · phone=d.ph||d.p · email=d.emailPrimary||d.em||
 *   d.email · kind=d.wa · data=the whole v1 contact object.
 * Type display is v1's cType(): C_CTYPE[contractorType] || C_WA[wa] || r. */
import { http } from "../../lib/http";
import { idbGet, idbSet } from "../../lib/idb";
import { SUPABASE_URL, SUPABASE_ANON_KEY } from "../../config";
import type { RecOp } from "../../lib/outbox";

export interface Contact {
  natKey: string;
  name: string;
  company: string | null;
  phone: string | null;
  email: string | null;
  type: string | null; // v1 cType() result
  tags: string[];
  updatedAt: number;
  // detail-view fields (v1 contact record, read-only in v2)
  phoneWork: string | null;
  phoneHome: string | null;
  emailSecondary: string | null;
  profCode: string | null; // good|mid|bad
  sourceCode: string | null; // C_SRC code
  status: string | null; // active|inactive|do_not_contact
  rating: number; // 0–5
  workState: string | null;
  counties: string[];
  cities: string[];
  website: string | null;
  linkedin: string | null;
  notes: string | null;
  address: string | null; // street (used by v1's completeness score)
  props: number; // v1 legacy linked-props counter (score input)
  /** the untouched v1 contact object (rec_contacts.data) — edits and merges
   * write THIS back so fields v2 doesn't model survive round-trips. */
  raw: Record<string, unknown>;
}

const CACHE = "contacts_v1";

/** v1 label maps (transactions.js): code → label. */
const C_WA: Record<string, string> = { wholesaler: "Wholesaler", agent: "Agent" };
const C_CTYPE: Record<string, string> = {
  gc: "GC", like_gc: "Like GC", electrician: "Electrician", plumbing: "Plumbing",
  roof: "Roof", ac: "A/C", yard: "Yard", inspector: "Inspector",
  refinisher: "Refinisher", floors: "Floors", staging: "Staging", photographer: "Photographer",
};

/** v1 option lists (transactions.js 580-585, verbatim pairs code→label). */
export const C_PROF: [string, string][] = [["good", "Good"], ["mid", "Mid"], ["bad", "Bad"]];
export const C_SRC: [string, string][] = [
  ["investorlift", "InvestorLift"], ["zillow", "Zillow"], ["realtor_com", "Realtor.com"],
  ["redfin", "Redfin"], ["facebook", "Facebook"], ["instagram", "Instagram"],
  ["linkedin", "LinkedIn"], ["craigslist", "Craigslist"], ["thumbtack", "Thumbtack"],
  ["angies_list", "Angie's List"], ["homeadvisor", "HomeAdvisor"], ["referral", "Referral"],
  ["cold_call", "Cold Call"], ["direct_mail", "Direct Mail"], ["networking_event", "Networking Event"],
  ["mls", "MLS"], ["auction", "Auction"], ["driving_for_dollars", "Driving for Dollars"], ["other", "Other"],
];
export const C_TAGOPTS = [
  "VIP", "Hot Lead", "Investor", "Cash Buyer", "First-Time Buyer", "Motivated Seller",
  "Repeat Client", "High Priority", "Follow Up", "Pre-Approved", "Needs Financing",
  "Contractor", "Vendor", "Partner",
];

/** v1 clabel(): code → label from a pair list ('' when unknown). */
export const clabel = (pairs: [string, string][], v: string | null): string | null => {
  if (!v) return null;
  const f = pairs.find((p) => p[0] === v);
  return f ? f[1] : null;
};

const s = (v: unknown): string | null => (typeof v === "string" && v.trim() ? v.trim() : null);
const strArr = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === "string" && !!x.trim()) : [];

/** v1 cType(c): contractor-type label, else work-area label, else free-text r. */
export function contactType(kind: string | null, data: Record<string, unknown>): string | null {
  const ct = s(data["contractorType"]);
  if (ct && C_CTYPE[ct]) return C_CTYPE[ct];
  const wa = kind ?? s(data["wa"]);
  if (wa && C_WA[wa]) return C_WA[wa];
  return s(data["r"]);
}

export function narrowContact(row: Record<string, unknown>): Contact | null {
  const nk = s(row["nat_key"]);
  if (!nk) return null;
  const d = row["data"];
  const data = d !== null && typeof d === "object" && !Array.isArray(d) ? (d as Record<string, unknown>) : {};
  const name = s(row["name"]) ?? s(data["n"]) ?? nk;
  const updated = s(row["updated_at"]);
  const t = updated ? Date.parse(updated) : NaN;
  const rawTags = data["tags"];
  const tags = Array.isArray(rawTags) ? rawTags.filter((x): x is string => typeof x === "string" && !!x.trim()).slice(0, 6) : [];
  const ratingRaw = data["rating"];
  const propsRaw = data["props"];
  return {
    natKey: nk,
    name,
    company: s(row["company"]) ?? s(data["co"]),
    phone: s(row["phone"]) ?? s(data["ph"]) ?? s(data["p"]),
    email: s(row["email"]) ?? s(data["emailPrimary"]) ?? s(data["em"]),
    type: contactType(s(row["kind"]), data),
    tags,
    updatedAt: Number.isNaN(t) ? 0 : t,
    phoneWork: s(data["phoneWork"]),
    phoneHome: s(data["phoneHome"]),
    emailSecondary: s(data["emailSecondary"]),
    profCode: s(data["prof"]),
    sourceCode: s(data["source"]),
    status: s(data["status"]),
    rating: typeof ratingRaw === "number" && ratingRaw > 0 ? Math.min(5, Math.round(ratingRaw)) : 0,
    workState: s(data["workState"]),
    counties: strArr(data["counties"]),
    cities: strArr(data["cities"]),
    website: s(data["website"]),
    linkedin: s(data["linkedin"]),
    notes: s(data["notes"]),
    address: s(data["address"]),
    props: typeof propsRaw === "number" && propsRaw > 0 ? propsRaw : 0,
    raw: data,
  };
}

// ---- writes (Phase 11 door: contacts put/patch/del) -------------------------

/** v1 _normNum verbatim: +1XXXXXXXXXX for US shapes; UNKNOWN formats stay
 * exactly as typed (slicing to last-10 once invented a different number). */
export function normNum(n: unknown): string {
  if (!n) return "";
  const sv = String(n).replace(/[^\d+]/g, "");
  if (!sv) return "";
  if (sv[0] === "+") return sv;
  if (sv.length === 11 && sv[0] === "1") return "+" + sv;
  if (sv.length === 10) return "+1" + sv;
  return sv;
}

/** v1 _recKeyContact — mirrors the SQL trigger's nat_key exactly:
 * lower(trim(name)) | last-10-digits(phone). */
export function contactNatKey(name: string, phone: unknown): string | null {
  if (!String(name ?? "").trim()) return null;
  const d = String(phone ?? "").replace(/[^0-9]/g, "").slice(-10);
  return String(name).trim().toLowerCase() + "|" + d;
}

/** The modal's draft — v1 CDRAFT, field for field. */
export interface ContactDraft {
  n: string; co: string; wa: string; contractorType: string; prof: string; source: string;
  emailPrimary: string; emailSecondary: string; ph: string; phoneWork: string; phoneHome: string;
  workState: string; counties: string[]; cities: string[];
  address: string; city: string; state: string; zip: string; website: string; linkedin: string;
  tags: string[]; notes: string; status: string; rating: number;
  preferred: string; bestTime: string; birthday: string; followUp: string;
}

export const emptyDraft = (): ContactDraft => ({
  n: "", co: "", wa: "", contractorType: "", prof: "", source: "",
  emailPrimary: "", emailSecondary: "", ph: "", phoneWork: "", phoneHome: "",
  workState: "", counties: [], cities: [], address: "", city: "", state: "", zip: "",
  website: "", linkedin: "", tags: [], notes: "", status: "active", rating: 0,
  preferred: "", bestTime: "", birthday: "", followUp: "",
});

/** v1 cInit(i): prefill the draft from an existing contact's raw object. */
export function draftFrom(c: Contact): ContactDraft {
  const r = c.raw;
  const sv = (k: string): string => (typeof r[k] === "string" ? (r[k] as string) : "");
  const arr = (k: string): string[] => strArr(r[k]);
  return {
    n: c.name, co: sv("co"), wa: sv("wa"), contractorType: sv("contractorType"), prof: sv("prof"),
    source: sv("source"), emailPrimary: c.email ?? "", emailSecondary: sv("emailSecondary"),
    ph: c.phone ?? "", phoneWork: sv("phoneWork"), phoneHome: sv("phoneHome"),
    workState: sv("workState"), counties: arr("counties"), cities: arr("cities"),
    address: sv("address"), city: sv("city"), state: sv("state"), zip: sv("zip"),
    website: sv("website"), linkedin: sv("linkedin"), tags: [...c.tags], notes: sv("notes"),
    status: c.status ?? "active", rating: c.rating,
    preferred: sv("preferred"), bestTime: sv("bestTime"), birthday: sv("birthday"), followUp: sv("followUp"),
  };
}

/** v1 saveContact's rec: normalized phones, em mirror, r = cType || 'Other'. */
export function buildContactRecord(d: ContactDraft, base?: Record<string, unknown>): Record<string, unknown> {
  const r = contactType(null, { contractorType: d.contractorType, wa: d.wa }) ?? "Other";
  return {
    ...(base ?? {}), // keep unmodeled v1 fields on edit round-trips
    n: d.n.trim(), co: d.co, wa: d.wa, contractorType: d.contractorType, prof: d.prof,
    source: d.source, emailPrimary: d.emailPrimary, em: d.emailPrimary, emailSecondary: d.emailSecondary,
    ph: d.ph ? normNum(d.ph) : "", phoneWork: d.phoneWork ? normNum(d.phoneWork) : "",
    phoneHome: d.phoneHome ? normNum(d.phoneHome) : "",
    workState: d.workState, counties: [...d.counties], cities: [...d.cities],
    address: d.address, city: d.city, state: d.state, zip: d.zip,
    website: d.website, linkedin: d.linkedin, tags: [...d.tags], notes: d.notes,
    status: d.status, rating: d.rating, preferred: d.preferred, bestTime: d.bestTime,
    birthday: d.birthday, followUp: d.followUp,
    props: typeof base?.["props"] === "number" ? base["props"] : 0,
    r,
  };
}

const dupKeysOfRaw = (rec: Record<string, unknown>): string[] => {
  const ks: string[] = [];
  const ph = last10(rec["ph"] ?? rec["p"]);
  if (ph) ks.push("ph:" + ph);
  const em = dnorm(rec["emailPrimary"] ?? rec["em"] ?? rec["email"]);
  if (em) ks.push("em:" + em);
  if (dnorm(rec["n"])) ks.push("nm:" + dnorm(rec["n"]) + "|" + dnorm(rec["co"]));
  return ks;
};

/** Save (create or edit): put under the record's key; if an edit changed the
 * name/phone the key moves → del the old row too. Saving un-tombstones the
 * record's identity keys (v1 saveContact rule). */
export function contactOpsForSave(rec: Record<string, unknown>, oldKey: string | null): { ops: RecOp[]; newKey: string } | null {
  const newKey = contactNatKey(String(rec["n"] ?? ""), rec["ph"]);
  if (!newKey) return null;
  const ops: RecOp[] = [];
  if (oldKey && oldKey !== newKey) ops.push({ s: "contacts", op: "del", k: oldKey });
  ops.push({ s: "contacts", op: "put", k: newKey, d: rec });
  const keys = dupKeysOfRaw(rec);
  if (keys.length) ops.push({ s: "tombs", op: "del", k: "contact", d: keys });
  return { ops, newKey };
}

/** Delete: tombstone the identity keys (so backfills can't resurrect — the
 * Kanda Power rule), then soft-delete the row. */
export function contactOpsForDelete(c: Contact): RecOp[] {
  const ops: RecOp[] = [];
  const keys = contactDupKeys(c);
  if (keys.length) ops.push({ s: "tombs", op: "add", k: "contact", d: keys });
  ops.push({ s: "contacts", op: "del", k: c.natKey });
  return ops;
}

/** v1 _cMergeInto verbatim over raw records: different phones cascade
 * ph→phoneWork→phoneHome→notes; extra emails →secondary→notes; tags/counties/
 * cities union; unique note lines append; props max; blanks fill. */
export function mergeContactsRaw(keeper: Record<string, unknown>, losers: Record<string, unknown>[]): Record<string, unknown> {
  const K: Record<string, unknown> = { ...keeper };
  const nph = (v: unknown) => String(v ?? "").replace(/\D/g, "").slice(-10);
  const nem = (v: unknown) => String(v ?? "").toLowerCase().trim();
  const sv = (v: unknown): string => (typeof v === "string" ? v : "");
  for (const c of losers) {
    for (const pv of [c["ph"], c["phoneWork"], c["phoneHome"]].filter(Boolean)) {
      const have = [K["ph"], K["phoneWork"], K["phoneHome"]].filter(Boolean).map(nph);
      if (have.includes(nph(pv))) continue;
      if (!K["ph"]) K["ph"] = pv;
      else if (!K["phoneWork"]) K["phoneWork"] = pv;
      else if (!K["phoneHome"]) K["phoneHome"] = pv;
      else K["notes"] = (sv(K["notes"]) ? sv(K["notes"]) + "\n" : "") + "Other phone: " + String(pv);
    }
    for (const evd of [c["emailPrimary"] ?? c["em"], c["emailSecondary"]].filter(Boolean)) {
      const have = [K["emailPrimary"] ?? K["em"], K["emailSecondary"]].filter(Boolean).map(nem);
      if (have.includes(nem(evd))) continue;
      if (!(K["emailPrimary"] ?? K["em"])) {
        K["emailPrimary"] = evd;
        K["em"] = evd;
      } else if (!K["emailSecondary"]) K["emailSecondary"] = evd;
      else K["notes"] = (sv(K["notes"]) ? sv(K["notes"]) + "\n" : "") + "Other email: " + String(evd);
    }
    for (const key of Object.keys(c)) {
      if (["ph", "phoneWork", "phoneHome", "emailPrimary", "em", "emailSecondary"].includes(key)) continue;
      if (key === "tags" || key === "counties" || key === "cities") {
        K[key] = [...new Set([...strArr(K[key]), ...strArr(c[key])])];
        continue;
      }
      if (key === "notes") {
        const mine = sv(K["notes"]);
        for (const line of sv(c["notes"]).split("\n")) {
          if (line.trim() && mine.indexOf(line.trim()) < 0) K["notes"] = (sv(K["notes"]) ? sv(K["notes"]) + "\n" : "") + line;
        }
        continue;
      }
      if (key === "props") {
        K["props"] = Math.max(Number(K["props"]) || 0, Number(c["props"]) || 0);
        continue;
      }
      const cur = K[key];
      if (cur === null || cur === undefined || cur === "" || (Array.isArray(cur) && cur.length === 0)) K[key] = c[key];
    }
  }
  return K;
}

/** Merge group → ops: losers tombstoned + deleted; keeper rewritten under its
 * (possibly moved) key; keeper's identity keys un-tombstoned. */
export function contactOpsForMerge(keeper: Contact, losers: Contact[]): { ops: RecOp[]; merged: Record<string, unknown> } | null {
  const merged = mergeContactsRaw(keeper.raw, losers.map((l) => l.raw));
  const saved = contactOpsForSave(merged, keeper.natKey);
  if (!saved) return null;
  const ops: RecOp[] = [];
  const loserKeys = [...new Set(losers.flatMap((l) => contactDupKeys(l)))];
  if (loserKeys.length) ops.push({ s: "tombs", op: "add", k: "contact", d: loserKeys });
  for (const l of losers) ops.push({ s: "contacts", op: "del", k: l.natKey });
  ops.push(...saved.ops);
  return { ops, merged };
}

// ---- duplicate finder (v1 contacts.js _cDupGroups/_cScore, read-only) -------

const dnorm = (v: unknown): string => String(v ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");
const last10 = (v: unknown): string => String(v ?? "").replace(/[^0-9]/g, "").slice(-10);

/** v1 _contactKeys: identity keys for dupe detection & tombstones. */
export function contactDupKeys(c: Contact): string[] {
  const ks: string[] = [];
  const ph = last10(c.phone);
  if (ph) ks.push("ph:" + ph);
  const em = dnorm(c.email);
  if (em) ks.push("em:" + em);
  if (dnorm(c.name)) ks.push("nm:" + dnorm(c.name) + "|" + dnorm(c.company));
  return ks;
}

/** v1 _cDupGroups: union-find over shared phone / email / name+company.
 * Returns groups of INDICES into rows, only groups with 2+ members. */
export function dupGroups(rows: Contact[]): number[][] {
  const byKey = new Map<string, number>();
  const parent: number[] = rows.map((_, i) => i);
  const find = (i: number): number => {
    while (parent[i] !== i) {
      parent[i] = parent[parent[i]!]!;
      i = parent[i]!;
    }
    return i;
  };
  const uni = (a: number, b: number) => {
    a = find(a);
    b = find(b);
    if (a !== b) parent[b] = a;
  };
  rows.forEach((c, i) => {
    for (const k of contactDupKeys(c)) {
      const prev = byKey.get(k);
      if (prev !== undefined) uni(prev, i);
      else byKey.set(k, i);
    }
  });
  const groups = new Map<number, number[]>();
  rows.forEach((_, i) => {
    const r = find(i);
    const g = groups.get(r);
    if (g) g.push(i);
    else groups.set(r, [i]);
  });
  return [...groups.values()].filter((g) => g.length > 1);
}

/** v1 _cScore: completeness — most-complete record is the suggested keeper. */
export function contactScore(c: Contact): number {
  let sc = 0;
  const filled: (string | number | null)[] = [
    c.name, c.company, c.phone, c.phoneWork, c.email, c.address, c.website, c.notes, c.sourceCode, c.profCode,
  ];
  for (const v of filled) if (v) sc++;
  return sc + c.tags.length * 0.2 + c.props * 0.1;
}

// ---- linked deals (v1 contactDeals, pure over v2 state) ---------------------

interface SellerObj {
  phone?: unknown;
  email?: unknown;
  name?: unknown;
}

/** v1 contactDeals: deals (transactions + wholesale) whose lead edits carry
 * this contact's phone / email / name via dispo or seller fields. Lead-only
 * contacts never match (a lead that was never converted doesn't count). */
export function contactDealAddresses(
  c: Contact,
  dealAddresses: string[],
  edits: Map<string, Record<string, unknown>>,
): string[] {
  const nph = last10(c.phone);
  const em = dnorm(c.email);
  const nm = dnorm(c.name);
  const out: string[] = [];
  const seen = new Set<string>();
  for (const a of dealAddresses) {
    if (!a) continue;
    const k = String(a).toLowerCase().trim();
    if (seen.has(k)) continue;
    seen.add(k);
    const ed = edits.get(k) ?? {};
    const sellerRaw = ed["seller"];
    const seller: SellerObj = sellerRaw !== null && typeof sellerRaw === "object" && !Array.isArray(sellerRaw) ? (sellerRaw as SellerObj) : {};
    const phs = [ed["dispPhone"], seller.phone].map(last10).filter(Boolean);
    const ems = [ed["dispEmail"], seller.email].map(dnorm).filter(Boolean);
    const nms = [ed["dispName"], seller.name].map(dnorm).filter(Boolean);
    if ((nph && phs.includes(nph)) || (em && ems.includes(em)) || (nm.length > 2 && nms.includes(nm))) out.push(a);
  }
  return out;
}

/** v2 improvement over v1's empty Comms tab: real threads matched by any of
 * the contact's phone numbers (last-10 digits, so +1 prefixes don't matter). */
export function contactPhoneSet(c: Contact): Set<string> {
  return new Set([c.phone, c.phoneWork, c.phoneHome].map(last10).filter(Boolean));
}

export async function cachedContacts(): Promise<Contact[] | null> {
  const c = await idbGet<{ at: number; rows: Contact[] }>(CACHE);
  return c && Array.isArray(c.rows) ? c.rows : null;
}

const PAGE = 1000; // PostgREST clamps any larger limit to 1000 — page, don't ask

export async function fetchContacts(token: string, signal?: AbortSignal): Promise<{ rows: Contact[]; dropped: number }> {
  // Tom has 3,229 contacts (live count 2026-07-16): a single request silently
  // truncates at the server's 1000-row cap, so page until a short page.
  const rows: unknown[] = [];
  for (let off = 0; ; off += PAGE) {
    const page = await http<unknown[]>(
      `${SUPABASE_URL}/rest/v1/rec_contacts?select=nat_key,name,company,phone,email,kind,data,updated_at&deleted_at=is.null&order=updated_at.desc,id.desc&limit=${PAGE}&offset=${off}`,
      { headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${token}` }, signal, timeoutMs: 20000 },
    );
    if (!Array.isArray(page)) break;
    rows.push(...page);
    if (page.length < PAGE) break;
    if (off >= 19 * PAGE) break; // hard ceiling — something is wrong
  }
  let dropped = 0;
  const out: Contact[] = [];
  const seen = new Set<string>();
  for (const r of Array.isArray(rows) ? rows : []) {
    const c = narrowContact(r as Record<string, unknown>);
    if (!c) {
      dropped++;
      continue;
    }
    if (seen.has(c.natKey)) continue;
    seen.add(c.natKey);
    out.push(c);
  }
  await idbSet(CACHE, { at: Date.now(), rows: out });
  return { rows: out, dropped };
}
